import React, { useState, useEffect } from 'react';
import { Link, useNavigate, useLocation } from 'react-router-dom';
import Helmet from 'react-helmet';
import Icon from '../../components/AppIcon';
import Button from '../../components/ui/Button';
import Header from '../../components/ui/Header';

import FeatureAccessIndicator from '../../components/ui/FeatureAccessIndicator';
import PlanHeader from './components/PlanHeader';
import PlanSection from './components/PlanSection';
import PlanActions from './components/PlanActions';
import AdSense from '../../components/AdSense';

// Import AI services
import { generateCareerPlan, regenerateCareerPlan, validateOpenAIConnection } from '../../services/aiPlanService';
import { isOpenAIConfigured } from '../../services/openaiClient';
import { exportPlan } from '../../utils/exportUtils';
import { loadAssessmentProgress } from '../../services/assessmentService';
import { saveCareerPlan, loadCareerPlan } from '../../services/careerPlanService';
import Footer from '../../components/Footer';
import { useAuth } from '../../contexts/AuthContext';
import progressTrackingService from '../../services/progressTrackingService';
import ApiKeyTest from '../../components/ApiKeyTest';


const AIPlanGeneration = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const { user, isPremium, isAuthenticated, signOut } = useAuth();

  // Component state
  const [plan, setPlan] = useState(null);
  const [expandedSections, setExpandedSections] = useState({ summary: true });
  const [isLoading, setIsLoading] = useState(true);
  const [isSaving, setIsSaving] = useState(false);
  const [isRegenerating, setIsRegenerating] = useState(false);
  const [progressPercentage, setProgressPercentage] = useState(0);
  const [error, setError] = useState(null);
  const [isApiConnected, setIsApiConnected] = useState(false);

  // Initialize plan data using OpenAI with improved error handling
  useEffect(() => {
    const loadPlan = async () => {
      setIsLoading(true);
      setError(null);
      
      try {
        // Check OpenAI configuration first
        const isConfigured = isOpenAIConfigured();
        if (!isConfigured) {
          setIsApiConnected(false);
          throw new Error('AI Plan Generation is temporarily unavailable. Please try again later or contact support if this issue persists.');
        }

        // Validate OpenAI connection
        const isConnected = await validateOpenAIConnection();
        setIsApiConnected(isConnected);
        
        if (!isConnected) {
          throw new Error('Failed to connect to OpenAI API. Please check your API key and network connection.');
        }

        // Get assessment data from navigation state or load from database
        let assessmentSetup = location?.state?.assessmentSetup;
        let answers = location?.state?.answers;
        let attitudes = location?.state?.attitudes;
        const assessmentId = location?.state?.assessmentId;
        
        // If no assessment data in navigation state, try to load from database
        if (!assessmentSetup && user) {
          console.log('No assessment data in navigation state, loading from database...');
          // If specific assessment ID provided, load that assessment
          const dbResult = await loadAssessmentProgress(user.id, assessmentId);
          if (dbResult.success && dbResult.data && dbResult.data.is_completed) {
            assessmentSetup = dbResult.data.assessment_setup;
            answers = dbResult.data.answers;
            attitudes = dbResult.data.attitudes;
            console.log('Loaded assessment data from database:', dbResult.data);
          } else {
            // No completed assessment found
            throw new Error('No completed assessment found. Please complete your assessment first.');
          }
        }
        
        console.log('=== AI PLAN GENERATION - ASSESSMENT DATA ===');
        console.log('assessmentSetup:', assessmentSetup);
        console.log('answers:', answers);
        console.log('attitudes:', attitudes);
        console.log('===========================================');
        
        // Check if we have complete assessment data
        if (!assessmentSetup || !answers) {
          throw new Error('Assessment data is required to generate a career plan. Please complete your assessment first.');
        }

        // Combine the assessment data properly with geographical information
        const assessmentData = {
          occupation: assessmentSetup.occupation,
          seniority: assessmentSetup.seniority,
          industry: assessmentSetup.industry,
          location: {
            currentZipCode: assessmentSetup.currentZipCode || null,
            locationPreference: assessmentSetup.locationPreference || 'current_location',
            targetLocation: assessmentSetup.targetLocation || null
          },
          responses: {
            careerGoals: answers?.[1],
            challenges: answers?.[3],
            learningStyle: answers?.[4],
            timeCommitment: answers?.[5],
            skillRating: answers?.[2],
            improvementAreas: answers?.[3]
          },
          attitudes: attitudes || {},
          userId: user?.id,
          completedAt: location?.state?.completedAt
        };

        // First, try to load existing plan from database
        if (user) {
          console.log('Loading existing career plan from database...');
          const existingPlanResult = await loadCareerPlan(user.id);
          if (existingPlanResult.success && existingPlanResult.data) {
            console.log('Found existing plan in database:', existingPlanResult.data);
            setPlan(existingPlanResult.data);
            
            // Calculate progress based on completed exercises (mock for now)
            const completedExercises = 3;
            const totalExercises = existingPlanResult.data?.totalExercises || 12;
            setProgressPercentage(Math.round((completedExercises / totalExercises) * 100));
            return; // Exit early if we found an existing plan
          }
        }

        // If no existing plan, generate new one using OpenAI
        console.log('No existing plan found, generating new plan...');
        const generatedPlan = await generateCareerPlan(assessmentData);
        setPlan(generatedPlan);
        
        // Save the new plan to database immediately
        if (user) {
          console.log('Saving new plan to database...');
          const saveResult = await saveCareerPlan(user.id, generatedPlan, { role: isPremium ? 'premium' : 'free' });
          if (saveResult.success) {
            console.log('Plan saved to database successfully');
            
            // Mark AI plan as generated in progress tracking
            try {
              await progressTrackingService.markAIPlanGenerated(user.id);
              console.log('AI plan marked as generated');
            } catch (error) {
              console.error('Error marking AI plan as generated:', error);
            }
          } else {
            console.error('Failed to save plan to database:', saveResult.error);
          }
        }
        
        // Calculate progress based on completed exercises (mock for now)
        const completedExercises = 3;
        const totalExercises = generatedPlan?.totalExercises || 12;
        setProgressPercentage(Math.round((completedExercises / totalExercises) * 100));
        
      } catch (error) {
        console.error('Failed to load AI-generated plan:', error);
        setError(error?.message || 'An unexpected error occurred');
        setIsApiConnected(false);
        
        // Provide fallback plan with clear messaging
        const fallbackPlan = {
          id: 'fallback_plan',
          occupation: assessmentSetup?.occupation || 'Your Role',
          seniority: assessmentSetup?.seniority || 'Your Level',
          createdAt: new Date(),
          lastUpdated: new Date(),
          isSaved: false,
          estimatedWeeks: 8,
          estimatedHoursPerWeek: '3-5',
          totalExercises: 12,
          sections: [
            {
              type: 'summary',
              content: 'AI Plan Generation is temporarily unavailable. Our team is working to restore this feature. In the meantime, you can explore our other career development tools.',
              items: []
            },
            {
              type: 'skills',
              content: 'Technical Skills Development',
              items: [
                {
                  title: 'Advanced JavaScript/TypeScript',
                  description: 'Master modern JavaScript features and TypeScript for better code quality.',
                  timeframe: '2-3 weeks',
                  priority: 'High'
                },
                {
                  title: 'System Design Fundamentals',
                  description: 'Learn scalable system architecture patterns and best practices.',
                  timeframe: '4-5 weeks',
                  priority: 'High'
                }
              ]
            }
          ]
        };
        setPlan(fallbackPlan);
        setProgressPercentage(25);
      } finally {
        setIsLoading(false);
      }
    };

    loadPlan();
  }, [location?.state, user?.id]);

  // Handle section toggle
  const handleSectionToggle = (sectionType) => {
    setExpandedSections(prev => ({
      ...prev,
      [sectionType]: !prev?.[sectionType]
    }));
  };

  // Handle plan save
  const handleSavePlan = async (planData) => {
    if (!user) {
      navigate('/authentication?redirect=/ai-plan-generation');
      return;
    }

    setIsSaving(true);
    
    try {
      // Save plan to database
      const saveResult = await saveCareerPlan(user.id, planData || plan, { role: isPremium ? 'premium' : 'free' });
      
      if (saveResult.success) {
        setPlan(prev => ({
          ...prev,
          isSaved: true,
          lastUpdated: new Date()
        }));
        console.log('Plan saved to database successfully');
      } else {
        throw new Error(saveResult.error);
      }
    } catch (error) {
      console.error('Failed to save plan:', error);
      setError('Failed to save plan. Please try again.');
    } finally {
      setIsSaving(false);
    }
  };

  // Handle plan regeneration using OpenAI with improved error handling
  const handleRegeneratePlan = async () => {
    if (!isApiConnected || !isOpenAIConfigured()) {
      setError('Cannot regenerate plan: OpenAI API is not properly configured.');
      return;
    }

    setIsRegenerating(true);
    setError(null);
    
    try {
      const userFeedback = {
        comments: 'Please provide more specific technical skills and updated industry trends',
        focusAreas: ['technical leadership', 'system architecture', 'team management']
      };
      
      const updatedPlan = await regenerateCareerPlan(plan, userFeedback);
      setPlan(updatedPlan);
      
      // Save the regenerated plan to database
      if (user) {
        const saveResult = await saveCareerPlan(user.id, updatedPlan, { role: isPremium ? 'premium' : 'free' });
        if (saveResult.success) {
          console.log('Regenerated plan saved to database successfully');
        } else {
          console.error('Failed to save regenerated plan to database:', saveResult.error);
        }
      }
      
      console.log('Plan regenerated successfully with OpenAI');
    } catch (error) {
      console.error('Failed to regenerate plan:', error);
      setError(error?.message || 'Failed to regenerate plan. Please try again.');
    } finally {
      setIsRegenerating(false);
    }
  };

  // Handle plan export with proper implementation
  const handleExportPlan = async (format) => {
      // Export is now available to all users since AI Plan is free

    if (!plan) {
      setError('No plan available for export.');
      return;
    }

    try {
      setError(null);
      
      // Show loading state in UI (optional)
      const exportButton = document.querySelector(`[data-export-${format}]`);
      if (exportButton) {
        exportButton.disabled = true;
        exportButton.textContent = `Exporting ${format?.toUpperCase()}...`;
      }

      // Use the proper export utility
      await exportPlan(plan, format);
      
      console.log(`Plan exported successfully as ${format?.toUpperCase()}`);
      
      // Optional: Show success message
      // You could add a toast notification here
      
    } catch (error) {
      console.error('Failed to export plan:', error);
      setError(error?.message || `Failed to export plan as ${format?.toUpperCase()}. Please try again.`);
    } finally {
      // Reset button state
      const exportButton = document.querySelector(`[data-export-${format}]`);
      if (exportButton) {
        exportButton.disabled = false;
        exportButton.textContent = `Export as ${format?.toUpperCase()}`;
      }
    }
  };

  // Handle authentication requirement
  const handleAuthRequired = () => {
    navigate('/authentication?redirect=/ai-plan-generation');
  };

  // Handle logout
  const handleLogout = () => {
    setUser(null);
    navigate('/');
  };

  // Loading state
  if (isLoading) {
    return (
      <div className="min-h-screen bg-background">
        <Header 
          user={user} 
          onAuthRequired={handleAuthRequired}
          onLogout={() => signOut()}
        />
        <main className="pt-16">
          <div className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
            <div className="space-y-6">
              {/* AI Generation Loading Header */}
              <div className="bg-card border border-border rounded-lg p-6">
                <div className="animate-pulse space-y-4">
                  <div className="flex items-center space-x-4">
                    <div className="w-12 h-12 bg-primary/20 rounded-lg flex items-center justify-center">
                      <Icon name="Sparkles" size={24} className="text-primary animate-spin" />
                    </div>
                    <div className="flex-1 space-y-2">
                      <div className="h-6 bg-muted rounded w-2/3"></div>
                      <div className="h-4 bg-muted rounded w-1/2"></div>
                      <div className="text-sm text-muted-foreground">
                        🤖 AI is generating your personalized career plan...
                      </div>
                    </div>
                    <div className="w-32 h-32 bg-muted rounded-full"></div>
                  </div>
                </div>
              </div>

              {/* Loading Sections */}
              {[1, 2, 3]?.map((i) => (
                <div key={i} className="bg-card border border-border rounded-lg p-6">
                  <div className="animate-pulse space-y-3">
                    <div className="h-6 bg-muted rounded w-1/4"></div>
                    <div className="h-4 bg-muted rounded w-3/4"></div>
                    <div className="h-4 bg-muted rounded w-1/2"></div>
                  </div>
                </div>
              ))}
            </div>
          </div>
        </main>
      </div>
    );
  }

  // Show assessment required message if no assessment data
  if (error && (error.includes('Assessment data is required') || error.includes('No completed assessment found'))) {
    return (
      <div className="min-h-screen bg-background">
        <Header 
          user={user} 
          onAuthRequired={handleAuthRequired}
          onLogout={() => signOut()}
        />
        <main className="pt-32 pb-16">
          <div className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
            {/* Page Header */}
            <div className="text-center mb-2">
              <div className="flex items-center justify-center space-x-2 mb-4">
                <Icon name="CheckCircle" size={32} className="text-primary" />
                <h1 className="text-3xl font-bold text-foreground">
                  AI Plan
                </h1>
              </div>
              <p className="text-muted-foreground">
                Generate personalized career development plans using AI technology tailored to your professional goals and current skill level.
              </p>
            </div>

            {/* Page Audio */}
            <div className="flex justify-center mb-2">
              <audio
                src="/assets/audio/AI_Plan.mp3"
                controls
                preload="metadata"
                className="h-8 max-w-xs"
              />
            </div>

            {/* AdSense - Banner for Free Users */}
            {!isPremium && (
              <div className="mb-2">
                <AdSense 
                  adSlot="1234567890"
                  adFormat="auto"
                  className="w-full"
                />
              </div>
            )}

            <div className="max-w-md mx-auto text-center space-y-6">
              <div className="w-16 h-16 bg-primary/10 rounded-full flex items-center justify-center mx-auto">
                <Icon name="FileText" size={32} className="text-primary" />
              </div>
              <div>
                <h1 className="text-2xl font-semibold text-foreground mb-2">
                  Complete Your Assessment First
                </h1>
                <p className="text-muted-foreground mb-6">
                  Your personalized career development plan requires your completed assessment data. 
                  Please complete your assessment to generate a tailored plan based on your specific goals and situation.
                </p>
              </div>
              <div className="flex flex-col sm:flex-row gap-3 justify-center">
                <Button
                  onClick={() => navigate('/career-assessment-setup')}
                  className="bg-primary hover:bg-primary/90"
                >
                  <Icon name="Play" size={16} className="mr-2" />
                  Start Assessment
                </Button>
                <Button
                  variant="outline"
                  onClick={() => navigate('/progress-dashboard')}
                >
                  <Icon name="BarChart3" size={16} className="mr-2" />
                  View Dashboard
                </Button>
              </div>
            </div>

            {/* AdSense - Bottom Banner for Free Users */}
            {!isPremium && (
              <div className="mt-8">
                <AdSense 
                  adSlot="3456789012"
                  adFormat="auto"
                  className="w-full"
                />
              </div>
            )}
          </div>
        </main>
        <Footer />
      </div>
    );
  }

  // No plan or error state with improved messaging
  if (!plan || error) {
    return (
      <div className="min-h-screen bg-background">
        <Header 
          user={user} 
          onAuthRequired={handleAuthRequired}
          onLogout={() => signOut()}
        />
        <main className="pt-32 pb-16">
          <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 py-16">
            {/* Page Header */}
            <div className="text-center mb-2">
              <div className="flex items-center justify-center space-x-2 mb-4">
                <Icon name="CheckCircle" size={32} className="text-primary" />
                <h1 className="text-3xl font-bold text-foreground">
                  AI Plan
                </h1>
              </div>
              <p className="text-muted-foreground">
                Generate personalized career development plans using AI technology tailored to your professional goals and current skill level.
              </p>
            </div>

            {/* Page Audio */}
            <div className="flex justify-center mb-2">
              <audio
                src="/assets/audio/AI_Plan.mp3"
                controls
                preload="metadata"
                className="h-8 max-w-xs"
              />
            </div>

            {/* AdSense - Banner for Free Users */}
            {!isPremium && (
              <div className="mb-2">
                <AdSense 
                  adSlot="1234567890"
                  adFormat="auto"
                  className="w-full"
                />
              </div>
            )}

            <div className="text-center space-y-6">
              {/* Live API Key Test */}
              <ApiKeyTest />
              
              <div className="w-16 h-16 bg-muted rounded-full flex items-center justify-center mx-auto">
                {error ? (
                  <Icon name="AlertTriangle" size={32} className="text-destructive" />
                ) : (
                  <Icon name="FileText" size={32} className="text-muted-foreground" />
                )}
              </div>
              
              <div className="space-y-2">
                <h1 className="text-2xl font-bold text-foreground">
                  {error ? 'AI Plan Generation Error' : 'No Plan Available'}
                </h1>
                <div className="text-muted-foreground max-w-md mx-auto space-y-2">
                  {error ? (
                    <>
                      <p className="text-destructive font-medium">{error}</p>
                      <div className="text-sm space-y-1">
                        <p>Possible solutions:</p>
                        <ul className="list-disc list-inside text-xs space-y-1">
                          <li>Ensure your OpenAI API key is set in environment variables</li>
                          <li>Check that your API key has sufficient credits/quota</li>
                          <li>Verify your network connection</li>
                          <li>Try refreshing the page</li>
                        </ul>
                      </div>
                    </>
                  ) : (
                    <p>Complete your career assessment to generate a personalized development plan.</p>
                  )}
                </div>
              </div>

              <div className="space-y-3">
                {error ? (
                  <Button
                    variant="default"
                    size="lg"
                    iconName="RefreshCw"
                    iconPosition="left"
                    onClick={() => window.location?.reload()}
                  >
                    Retry AI Generation
                  </Button>
                ) : (
                  <Button
                    variant="default"
                    size="lg"
                    iconName="ClipboardList"
                    iconPosition="left"
                    asChild
                  >
                    <Link to="/career-assessment-setup">
                      Start Assessment
                    </Link>
                  </Button>
                )}
                
                <Button
                  variant="outline"
                  size="default"
                  asChild
                >
                  <Link to="/progress-dashboard">
                    View Dashboard
                  </Link>
                </Button>
              </div>
            </div>

            {/* AdSense - Bottom Banner for Free Users */}
            {!isPremium && (
              <div className="mt-8">
                <AdSense 
                  adSlot="3456789012"
                  adFormat="auto"
                  className="w-full"
                />
              </div>
            )}
          </div>
        </main>
      </div>
    );
  }

  return (
    <>
      <Helmet>
        <title>AI Plan Generation - CareerLift</title>
        <meta name="description" content="Generate personalized career development plans using AI technology tailored to your professional goals and current skill level." />
      </Helmet>

      <div className="min-h-screen bg-background flex flex-col">
        <Header 
          user={user} 
          onAuthRequired={handleAuthRequired} 
          onLogout={handleLogout}
        />
        
        <main className="flex-1 pt-32 pb-16">
          <div className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
            <div className="space-y-8">
              {/* AI-Generated Plan Header */}
              <div className="bg-gradient-to-r from-primary/5 to-purple-500/5 border border-primary/20 rounded-lg p-4 mb-2">
                <div className="flex items-center space-x-3">
                  <Icon name="Sparkles" size={20} className="text-primary" />
                  <div>
                    <p className="text-sm font-medium text-primary">
                      🤖 AI-Generated Career Plan
                    </p>
                    <p className="text-xs text-muted-foreground">
                      Powered by OpenAI {isApiConnected ? 'GPT-4' : 'Fallback Mode'} • Generated on {new Date()?.toLocaleDateString()}
                    </p>
                  </div>
                  {!isApiConnected && (
                    <div className="ml-auto">
                      <span className="inline-flex items-center px-2 py-1 rounded-full text-xs bg-warning/10 text-warning border border-warning/20">
                        <Icon name="AlertTriangle" size={12} className="mr-1" />
                        API Limited
                      </span>
                    </div>
                  )}
                </div>
              </div>

              {/* Page Audio */}
              <div className="flex justify-center mb-6">
                <audio
                  src="/assets/audio/AI_Plan.mp3"
                  controls
                  preload="metadata"
                  className="h-8 max-w-xs"
                />
              </div>

              {/* Ad Placement 1 - Top Banner */}
              <div className="mb-6">
                <AdSense 
                  adSlot="1234567890"
                  adFormat="auto"
                  className="w-full"
                />
              </div>

              {/* Plan Header */}
              <PlanHeader 
                plan={plan}
                user={user ? { ...user, isPremium } : null}
                progressPercentage={progressPercentage}
              />

              {/* AI Plan is now available to all users - no premium restriction needed */}

              <div className="grid grid-cols-1 xl:grid-cols-3 gap-8">
                {/* Plan Sections */}
                <div className="xl:col-span-2 space-y-4">
                  {plan?.sections?.map((section) => (
                    <PlanSection
                      key={section?.type}
                      section={section}
                      isExpanded={expandedSections?.[section?.type] || false}
                      onToggle={() => handleSectionToggle(section?.type)}
                      isPremium={true}
                      showUpgradePrompt={false}
                    />
                  )) || (
                    <div className="bg-card border border-border rounded-lg p-6">
                      <p className="text-muted-foreground">No plan sections available.</p>
                    </div>
                  )}
                </div>

                {/* Actions Sidebar */}
                <div className="space-y-6">
                  <PlanActions
                    user={user}
                    plan={plan}
                    onSavePlan={handleSavePlan}
                    onRegeneratePlan={handleRegeneratePlan}
                    onExportPlan={handleExportPlan}
                    isSaving={isSaving}
                    isRegenerating={isRegenerating}
                  />

                  {/* Ad Placement 2 - Sidebar */}
                  <div className="bg-card border border-border rounded-lg p-4">
                    <AdSense 
                      adSlot="2345678901"
                      adFormat="rectangle"
                      className="w-full"
                    />
                  </div>

                  {/* AI Status Card */}
                  <div className="bg-card border border-border rounded-lg p-4 space-y-4">
                    <h3 className="font-semibold text-foreground flex items-center space-x-2">
                      <Icon name="Bot" size={18} />
                      <span>AI Status</span>
                    </h3>
                    
                    <div className="space-y-3">
                      <div className="flex items-center justify-between text-sm">
                        <span className="text-muted-foreground">OpenAI Connection</span>
                        <span className={`font-medium ${isApiConnected ? 'text-success' : 'text-destructive'}`}>
                          {isApiConnected ? '✅ Connected' : '❌ Disconnected'}
                        </span>
                      </div>
                      <div className="flex items-center justify-between text-sm">
                        <span className="text-muted-foreground">Model Used</span>
                        <span className="font-medium text-foreground">GPT-4 Mini</span>
                      </div>
                      <div className="flex items-center justify-between text-sm">
                        <span className="text-muted-foreground">Plan Quality</span>
                        <span className="font-medium text-primary">AI-Enhanced</span>
                      </div>
                      <div className="flex items-center justify-between text-sm">
                        <span className="text-muted-foreground">Last Updated</span>
                        <span className="font-medium text-foreground">
                          {plan?.lastUpdated ? new Date(plan.lastUpdated)?.toLocaleDateString() : 'Unknown'}
                        </span>
                      </div>
                    </div>
                  </div>

                  {/* Quick Stats */}
                  <div className="bg-card border border-border rounded-lg p-4 space-y-4">
                    <h3 className="font-semibold text-foreground flex items-center space-x-2">
                      <Icon name="TrendingUp" size={18} />
                      <span>Quick Stats</span>
                    </h3>
                    
                    <div className="space-y-3">
                      <div className="flex items-center justify-between text-sm">
                        <span className="text-muted-foreground">Completion Rate</span>
                        <span className="font-medium text-foreground">{progressPercentage}%</span>
                      </div>
                      <div className="flex items-center justify-between text-sm">
                        <span className="text-muted-foreground">Time Investment</span>
                        <span className="font-medium text-foreground">{plan?.estimatedHoursPerWeek || 'N/A'} hrs/week</span>
                      </div>
                      <div className="flex items-center justify-between text-sm">
                        <span className="text-muted-foreground">Target Timeline</span>
                        <span className="font-medium text-foreground">{plan?.estimatedWeeks || 'N/A'} weeks</span>
                      </div>
                      <div className="flex items-center justify-between text-sm">
                        <span className="text-muted-foreground">Active KPIs</span>
                        <span className="font-medium text-foreground">
                          {plan?.sections?.find(s => s?.type === 'kpis')?.metrics?.length || 0}
                        </span>
                      </div>
                    </div>
                  </div>

                  {/* Next Steps */}
                  <div className="bg-card border border-border rounded-lg p-4 space-y-4">
                    <h3 className="font-semibold text-foreground flex items-center space-x-2">
                      <Icon name="ArrowRight" size={18} />
                      <span>Next Steps</span>
                    </h3>
                    
                    <div className="space-y-3">
                      <Button
                        variant="outline"
                        size="sm"
                        fullWidth
                        iconName="Play"
                        iconPosition="left"
                        asChild
                      >
                        <Link to="/exercise-management">
                          Start Weekly Exercises
                        </Link>
                      </Button>
                      
                      <Button
                        variant="ghost"
                        size="sm"
                        fullWidth
                        iconName="BarChart3"
                        iconPosition="left"
                        asChild
                      >
                        <Link to="/progress-dashboard">
                          Track Progress
                        </Link>
                      </Button>
                      
                      {/* AI Plan is now free - no upgrade needed */}
                    </div>
                  </div>
                </div>
              </div>
            </div>

            {/* Ad Placement 3 - Bottom Banner */}
            <div className="mt-8">
              <AdSense 
                adSlot="3456789012"
                adFormat="auto"
                className="w-full"
              />
            </div>
          </div>
        </main>

        {/* Footer */}
        <Footer />
      </div>
    </>
  );
};

export default AIPlanGeneration;